import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import "./Warehouse.css";
import "./Suppliers.css";

export default function Supplier() {
  const { user, token, logout } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [supplierName, setSupplierName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");

  const [suppliers, setSuppliers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const location = useLocation();
  
  useEffect(() => {
    if (!token) return; 
    fetchSuppliers();
     
  }, [token]);


  const fetchSuppliers = async (forceFetch = false) => {
    try {
      setLoading(true);
      setError("");

      // Try load cached warehouses first
      if (!forceFetch) {
        const cachedSuppliers = sessionStorage.getItem("suppliers");
        if (cachedSuppliers) {
          const parsed = JSON.parse(cachedSuppliers);
          setSuppliers(parsed);
          return;
        }
      }

      const response = await fetch(`${API_BASE_URL}/suppliers`,
        {
          headers: {"Authorization": `Bearer ${token}`}
        }
      );
      if (!response.ok) throw new Error("Failed to fetch suppliers");

      const data = await response.json();
       
      setSuppliers(data);
      sessionStorage.setItem("suppliers", JSON.stringify(data));
    } catch (err) {
      setError(err.message);
    }
    finally {
      setLoading(false);
    }
  };
  
  
 useEffect(() => {
  const params = new URLSearchParams(location.search);
  if (params.get("add") === "true") {
    resetForm();
    setShowModal(true);
    window.history.replaceState({}, "", "/suppliers");
  }
}, [location.search]);

const resetForm = () => {
  setSupplierName("");
  setPhoneNumber("");
  setEmail("");
  setStreetAddress("");
  setCity("");
  setEditingSupplier(null);
};

// Small helper: delay
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Generic retry with exponential backoff
const fetchWithRetry = async (url, options = {}, retries = 4, baseDelay = 800) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, options);

      if (res.status === 429) {
        // Too many requests → wait and retry
        const jitter = Math.floor(Math.random() * 300);
        const wait = baseDelay * Math.pow(2, attempt - 1) + jitter;
        console.warn(`⚠️ Rate limited: retrying in ${wait}ms (attempt ${attempt})`);
        await delay(wait);
        continue;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (err) {
      if (attempt === retries) throw err;
      const wait = baseDelay * Math.pow(2, attempt - 1);
      console.warn(`Retrying ${url} (${attempt}/${retries}) after ${wait}ms`);
      await delay(wait);
    }
  }
};

const handleCreateAndEdit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError("");

  const supplierData = {
    name: supplierName,
    phoneNumber: phoneNumber,
    email: email,
    address: streetAddress,
    city: city,
    companyId: user.companyId
  };

  try {
    const method = editingSupplier ? "PUT" : "POST";
    const url = editingSupplier
      ? `${API_BASE_URL}/suppliers/${editingSupplier.id}` 
      : `${API_BASE_URL}/suppliers`;

    const response = await fetchWithRetry(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(supplierData),
    });

      const saved = await response.json();

      setSuppliers(prev => {
        const updated = editingSupplier
          ? prev.map(s => (s.id === saved.id ? saved : s))
          : [...prev, saved];

        // Update sessionStorage
        sessionStorage.setItem("suppliers", JSON.stringify(updated));

        return updated;
      });
      await fetchSuppliers(true);
      alert(editingSupplier ? "Supplier updated!" : "Supplier added!");

      resetForm();
      setShowModal(false);
    } catch (err) {
      console.error("Error saving wsupplier:", err);
      setError(err.message || "Failed to save supplier. Please try again later.");
    } finally {
      setLoading(false);
    }
  };


  const handleRemoveSupplier = async (id) => {
    if (!window.confirm("Are you sure you want to remove this supplier?"))
      return;

    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/suppliers/${id}`, { 
          method: "DELETE", 
          headers: {"Authorization": `Bearer ${token}`}
        });
      if (!res.ok) throw new Error("Failed to delete supplier");
      setSuppliers(prev => {
        const updated = prev.filter(w => w.id !== id);

        // Update sessionStorage
        sessionStorage.setItem("suppliers", JSON.stringify(updated));

        return updated;
      });
      await fetchSuppliers(true);
    } catch (err) {
      console.error(err);
      alert("Error deleting supplier.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSupplier = (supplier) => {
    setSupplierName(supplier.name);
    setPhoneNumber(supplier.phoneNumber);
    setEmail(supplier.email);
    setStreetAddress(supplier.address || "");
    setCity(supplier.city || "");
    setEditingSupplier(supplier);
    setShowModal(true);
  };

  const handleActivateSupplier = async (id) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/suppliers/${id}`, { 
          method: "PATCH", 
          headers: {"Authorization": `Bearer ${token}`}
        });
      if (!res.ok) throw new Error("Failed to activate supplier");
      await fetchSuppliers();
    } catch (err) {
      console.error(err);
      alert("Error deleting supplier.");
    } finally {
      setLoading(false);
    }
  };

  // Back to Dashboard Function
  const handleBackToDashboard = () => {
    window.location.href = '/dashboard';
   
  };

  const [filters, setFilters] = useState({
    search: "",
    city
  });
  
  const filteredSuppliers = suppliers.filter(s =>

    (!filters.search ||
      s.code.toLowerCase().includes(filters.search.toLowerCase()) ||
      s.name.toLowerCase().includes(filters.search.toLowerCase()) 
    ) &&
    
    (!filters.city || s.id === filters.city || s.city === filters.city)
  );

  return (
    <div className="warehouses-page">
      {/* Header with user info and logout */}
      <div className="info-header">
        <div>
          <h3 style={{ color: '#2d5016', margin: 0 }}>
            {user?.fullName || `${user?.firstName} ${user?.lastName}` || user?.email}
          </h3>
          <h3 style={{ color: '#2d5016', margin: 0 }}>{user?.companyName}</h3>
          <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>
            {user?.email} - {user?.role}
          </p>
        </div>
        <button 
          onClick={logout}
          style={{
            background: '#ff8c00',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Logout
        </button>
      </div>
      <div className="content-section">
        <div className="warehouses-header">
          <div className="header-title-section">
            <h1>Manage Suppliers</h1>
          </div>
          <div className="header-actions">
            <button 
              className="back-btn" 
              onClick={handleBackToDashboard}
              disabled={loading}
            >
              <ArrowLeft style={{ width: '16px', height: '16px' }} />
                Back to Dashboard
            </button>
            <button 
              className="add-btn" 
              onClick={() => {resetForm(); setShowModal(true);}}
              disabled={loading}
            >
              + Add Supplier
            </button>
          </div>
        </div>

        <div className="filters-section">
          <div className="form-group filter-group">
            <input
              type="text"
              placeholder=" "
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
            <label>Search suppliers...</label>
          </div>

          <div className="form-group filter-group">
            <select
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
            >
              <option value="">All</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.city}</option>
              ))}
            </select>
            <label>City</label>
          </div>
        </div>

        {error && (
          <div className="error-message">
            Error: {error}
          </div>
        )}

        <div className="warehouses-table-section">
          <h2>Suppliers in your system</h2>
          {loading && suppliers.length ? (
            <div className="loading">Loading suppliers...</div>
          ) : (
            <table className="warehouses-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Phone Number</th>
                  <th>Email</th>
                  <th>Street Address</th>
                  <th>City</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.length > 0 ? (
                  filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id}>
                      <td>{supplier.code}</td>
                      <td>{supplier.name}</td>
                      <td>{supplier.phoneNumber}</td>
                      <td>{supplier.email}</td>
                      <td>{supplier.address}</td>
                      <td>{supplier.city}</td>
                      <td>{supplier.isActive ? "Active" : "Inactive"}</td>
                      <td>
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditSupplier(supplier)}
                          disabled={loading}
                        >
                          Edit
                        </button>
                        <button 
                          className="activate-btn"
                          onClick={() => handleActivateSupplier(supplier.i)}
                          disabled={loading}
                        >
                          Activate
                        </button>
                        <button 
                          className="remove-btn" 
                          onClick={() => handleRemoveSupplier(supplier.id)}
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center" }}>
                      No supplier yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>
                {editingSupplier ? "Edit Supplier" : "Create a New Supplier"}
              </h3>
              <form onSubmit={handleCreateAndEdit} className="modal-form">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder=" "
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    required
                  />
                  <label>Supplier name</label>
                </div>

                <div className="form-group">
                  <input
                    type="tel"
                    placeholder=" "
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                  />
                  <label>Phone number</label>
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    placeholder=" "
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <label>Email</label>
                </div>

             
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder=" "
                      value={streetAddress}
                      onChange={(e) => setStreetAddress(e.target.value)}
                      disabled={loading}
                    />
                    <label>Street address</label>
                  </div>
                  
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder=" "
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      disabled={loading}
                    />
                    <label>City</label>
                  </div>


                <div className="modal-actions">
                  <button type="submit" disabled={loading}>
                    {editingSupplier ? "Save Changes" : "Create"}
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}